// NX-6284: Ireland "8% of worked hours (irregular)" holiday pay.
import { useAsync } from '@nxsys/hooks/useAsync';
import {
  getIrregularHolidayTimesheetViewApi,
  getIrregularHolidayWorkerSummaryApi,
  updateIrregularHolidayOpeningBalancesApi,
} from '@nxsys/services/api/irregularHoliday';
import {
  GetIrregularHolidayTimesheetViewResponse,
  GetIrregularHolidayWorkerSummaryResponse,
  IrregularHolidayTimesheetViewParams,
  UpdateIrregularHolidayOpeningBalancesParams,
  UpdateIrregularHolidayOpeningBalancesResponse,
} from '@nxsys/types/api';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';

// Payroll-settings-gated — the worker dashboard and worker settings screen,
// where the signed-in user already holds that permission to view the page.
export const useGetIrregularHolidayWorkerSummary = () => {
  const { value, loading, error, run } = useAsync<
    GetIrregularHolidayWorkerSummaryResponse,
    { id: string; workedHours?: number }
  >(({ id, workedHours }) =>
    getIrregularHolidayWorkerSummaryApi(id, workedHours),
  );

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  // `workedHours` is only meaningful for a leaver (P45) timesheet — see
  // TimesheetForm, which recomputes it from the non-PRHP work lines.
  const _run = (id: string, workedHours?: number) => run({ id, workedHours });

  return { value, loading, error, run: _run };
};

// `timesheets:create`-gated, not payroll-settings — TimesheetForm/AppRateForm
// must use this one, not useGetIrregularHolidayWorkerSummary, so an agency
// contact or a Timesheets-only company user can still add/edit a timesheet.
export const useGetIrregularHolidayTimesheetView = () => {
  const { value, loading, error, run } = useAsync<
    GetIrregularHolidayTimesheetViewResponse,
    { id: string; params?: IrregularHolidayTimesheetViewParams }
  >(({ id, params }) => getIrregularHolidayTimesheetViewApi(id, params));

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  // `params` names the timesheet's pay period (or the edited timesheet), so
  // the figures are the ones its save will be checked against.
  const _run = (id: string, params?: IrregularHolidayTimesheetViewParams) =>
    run({ id, params });

  return { value, loading, error, run: _run };
};

export const useUpdateIrregularHolidayOpeningBalances = () => {
  const { value, loading, error, run } = useAsync<
    UpdateIrregularHolidayOpeningBalancesResponse,
    UpdateIrregularHolidayOpeningBalancesParams & { id: string }
  >(({ id, ...params }) => updateIrregularHolidayOpeningBalancesApi(id, params));
  const _run = (
    id: string,
    params: UpdateIrregularHolidayOpeningBalancesParams,
  ) => run({ id, ...params });

  useEffect(() => {
    if (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [error]);

  return { value, loading, error, run: _run };
};
